import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Check, X, Trash2, LogOut, FileText, Mail, Copy } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { api } from "../lib/api";

const TOKEN_KEY = "ohs_admin_token";

export default function AdminPage() {
    const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
    const [authed, setAuthed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [recommendations, setRecommendations] = useState([]);
    const [downloads, setDownloads] = useState([]);
    const [busyId, setBusyId] = useState(null);
    const [tab, setTab] = useState("recommendations");

    const headers = { "X-Admin-Token": token };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [recRes, dlRes] = await Promise.all([
                api.get("/admin/recommendations", { headers }),
                api.get("/admin/guide-downloads", { headers }),
            ]);
            setRecommendations(recRes.data || []);
            setDownloads(dlRes.data || []);
            setAuthed(true);
        } catch (err) {
            if (err?.response?.status === 401) {
                toast.error("Invalid token");
                localStorage.removeItem(TOKEN_KEY);
                setAuthed(false);
            } else {
                toast.error("Could not load admin data");
            }
        } finally {
            setLoading(false);
        }
        // `headers` is intentionally omitted: it is derived from `token` (already in deps) and is rebuilt on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        if (token) load();
        // `load` and `token` are intentionally omitted: this effect must only run once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = async (e) => {
        e.preventDefault();
        if (!token) return;
        setLoading(true);
        try {
            await api.post("/admin/verify", { token });
            localStorage.setItem(TOKEN_KEY, token);
            setAuthed(true);
            await load();
            toast.success("Signed in");
        } catch (err) {
            toast.error("Invalid token");
        } finally {
            setLoading(false);
        }
    };

    const update = async (id, patch) => {
        setBusyId(id);
        try {
            await api.patch(`/admin/recommendations/${id}`, patch, { headers });
            await load();
            toast.success("Updated");
        } catch (err) {
            toast.error("Update failed");
        } finally {
            setBusyId(null);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Permanently delete this recommendation?")) return;
        setBusyId(id);
        try {
            await api.delete(`/admin/recommendations/${id}`, { headers });
            await load();
            toast.success("Deleted");
        } catch (err) {
            toast.error("Delete failed");
        } finally {
            setBusyId(null);
        }
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
        setRecommendations([]);
        setDownloads([]);
    };

    const deleteDownload = async (id) => {
        if (!window.confirm("Delete this download record?")) return;
        setBusyId(id);
        try {
            await api.delete(`/admin/guide-downloads/${id}`, { headers });
            await load();
            toast.success("Deleted");
        } catch (err) {
            toast.error("Delete failed");
        } finally {
            setBusyId(null);
        }
    };

    const copy = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => toast.success("Copied"));
    };

    if (!authed) {
        return (
            <div className="min-h-[80vh] flex items-center" data-testid="admin-login">
                <div className="mx-auto max-w-md w-full px-6 py-20">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                        Admin
                    </span>
                    <h1 className="font-display text-4xl mt-5 text-brand-navy">
                        Sign in to manage submissions.
                    </h1>
                    <form onSubmit={login} className="mt-8 space-y-5">
                        <div>
                            <Label htmlFor="token" className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                Admin token
                            </Label>
                            <Input
                                id="token"
                                type="password"
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                data-testid="admin-token-input"
                                className="mt-2 rounded-none border-brand-border focus-visible:ring-brand-navy"
                                autoFocus
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading || !token}
                            data-testid="admin-login-button"
                            className="rounded-none bg-brand-navy text-brand-stone hover:bg-brand-hover px-8 py-6 text-sm tracking-wide w-full"
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                        </Button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-page">
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-12 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                            Admin · Optimum Hospitality Solutions
                        </span>
                        <h1 className="font-display text-4xl mt-3 text-brand-navy">
                            {tab === "recommendations" ? (
                                <>{recommendations.length} recommendation{recommendations.length === 1 ? "" : "s"}</>
                            ) : (
                                <>{downloads.length} guide download{downloads.length === 1 ? "" : "s"}</>
                            )}
                        </h1>
                        <p className="mt-2 text-sm text-brand-ink/65">
                            {tab === "recommendations"
                                ? "Tick \"Publish\" to show a recommendation on the homepage. Anything unticked stays hidden."
                                : "Every Hospitality Audit Guide download captured from the website. Click an email to copy it."}
                        </p>
                    </div>
                    <button
                        onClick={logout}
                        className="inline-flex items-center gap-2 text-xs text-brand-ink/60 hover:text-brand-navy"
                        data-testid="admin-logout"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>

                <div className="mx-auto max-w-7xl px-6 lg:px-12 pb-4 flex gap-2" data-testid="admin-tabs">
                    <button
                        data-testid="admin-tab-recommendations"
                        onClick={() => setTab("recommendations")}
                        className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                            tab === "recommendations"
                                ? "bg-brand-navy text-brand-stone border-brand-navy"
                                : "bg-transparent text-brand-ink/70 border-brand-border hover:border-brand-champagne hover:text-brand-navy"
                        }`}
                    >
                        Recommendations
                        <span className="ml-2 text-[10px] opacity-70">{recommendations.length}</span>
                    </button>
                    <button
                        data-testid="admin-tab-guide-downloads"
                        onClick={() => setTab("downloads")}
                        className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                            tab === "downloads"
                                ? "bg-brand-navy text-brand-stone border-brand-navy"
                                : "bg-transparent text-brand-ink/70 border-brand-border hover:border-brand-champagne hover:text-brand-navy"
                        }`}
                    >
                        Guide Downloads
                        <span className="ml-2 text-[10px] opacity-70">{downloads.length}</span>
                    </button>
                </div>
            </section>

            {tab === "recommendations" && (
            <section>
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-12 space-y-6">
                    {loading && (
                        <div className="text-center py-16 text-brand-ink/60">
                            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        </div>
                    )}
                    {!loading && recommendations.length === 0 && (
                        <div className="border border-brand-border bg-white p-16 text-center text-brand-ink/60">
                            No recommendations yet.
                        </div>
                    )}
                    {!loading && recommendations.map((r) => (
                        <div
                            key={r.id}
                            data-testid={`admin-rec-${r.id}`}
                            className={`border ${r.published ? "border-brand-champagne" : "border-brand-border"} bg-white p-8`}
                        >
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                                <div className="lg:col-span-8">
                                    <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">
                                        {r.relationship}
                                    </div>
                                    <blockquote className="mt-3 font-display text-lg text-brand-navy leading-snug">
                                        &ldquo;{r.recommendation}&rdquo;
                                    </blockquote>
                                    <div className="mt-4 text-sm text-brand-ink/80">
                                        <strong>{r.name}</strong> · {r.role}
                                        {r.organisation ? ` · ${r.organisation}` : ""}
                                    </div>
                                    <div className="mt-1 text-xs text-brand-ink/55">
                                        {r.email} · submitted {new Date(r.created_at).toLocaleDateString("en-GB")}
                                    </div>
                                </div>

                                <div className="lg:col-span-4 space-y-3 lg:border-l lg:border-brand-border lg:pl-6">
                                    <div className="text-xs uppercase tracking-[0.2em] text-brand-ink/60">
                                        Permissions submitted
                                    </div>
                                    <div className="text-xs text-brand-ink/75 space-y-1">
                                        <div>OK to publish: <strong>{r.permission_to_publish ? "Yes" : "No"}</strong></div>
                                        <div>OK to use name: <strong>{r.allow_name ? "Yes" : "No (anonymise)"}</strong></div>
                                    </div>

                                    <div className="pt-3 mt-3 border-t border-brand-border flex flex-col gap-2">
                                        <Button
                                            onClick={() => update(r.id, { published: !r.published })}
                                            disabled={busyId === r.id}
                                            data-testid={`admin-rec-publish-${r.id}`}
                                            className={`rounded-none w-full text-xs tracking-wide ${
                                                r.published
                                                    ? "bg-white border border-brand-navy text-brand-navy hover:bg-brand-stone"
                                                    : "bg-brand-navy text-brand-stone hover:bg-brand-hover"
                                            }`}
                                        >
                                            {busyId === r.id ? (
                                                <Loader2 className="h-3 w-3 animate-spin" />
                                            ) : r.published ? (
                                                <span className="inline-flex items-center gap-2"><X size={14} /> Unpublish</span>
                                            ) : (
                                                <span className="inline-flex items-center gap-2"><Check size={14} /> Publish on site</span>
                                            )}
                                        </Button>

                                        <Button
                                            onClick={() => update(r.id, { allow_name: !r.allow_name })}
                                            disabled={busyId === r.id}
                                            data-testid={`admin-rec-name-${r.id}`}
                                            variant="outline"
                                            className="rounded-none w-full text-xs tracking-wide border-brand-border"
                                        >
                                            {r.allow_name ? "Anonymise" : "Show name"}
                                        </Button>

                                        <button
                                            onClick={() => remove(r.id)}
                                            disabled={busyId === r.id}
                                            data-testid={`admin-rec-delete-${r.id}`}
                                            className="text-xs text-brand-ink/50 hover:text-red-600 inline-flex items-center gap-1.5 self-end mt-1"
                                        >
                                            <Trash2 size={12} /> Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
            )}

            {tab === "downloads" && (
            <section data-testid="admin-downloads-section">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-12">
                    {loading && (
                        <div className="text-center py-16 text-brand-ink/60">
                            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        </div>
                    )}
                    {!loading && downloads.length === 0 && (
                        <div className="border border-brand-border bg-white p-16 text-center text-brand-ink/60" data-testid="admin-downloads-empty">
                            No guide downloads yet.
                        </div>
                    )}
                    {!loading && downloads.length > 0 && (
                        <div className="border border-brand-border bg-white overflow-x-auto">
                            <table className="w-full text-sm" data-testid="admin-downloads-table">
                                <thead className="bg-brand-stone/60 text-[10px] uppercase tracking-[0.2em] text-brand-ink/60">
                                    <tr>
                                        <th className="text-left px-5 py-3 font-medium">Name</th>
                                        <th className="text-left px-5 py-3 font-medium">Email</th>
                                        <th className="text-left px-5 py-3 font-medium">Organisation</th>
                                        <th className="text-left px-5 py-3 font-medium">Role</th>
                                        <th className="text-left px-5 py-3 font-medium">Captured</th>
                                        <th className="text-right px-5 py-3 font-medium"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {downloads.map((d) => (
                                        <tr
                                            key={d.id}
                                            data-testid={`admin-download-${d.id}`}
                                            className="border-t border-brand-border hover:bg-brand-stone/40"
                                        >
                                            <td className="px-5 py-3 text-brand-navy font-medium flex items-center gap-2">
                                                <FileText size={14} className="text-brand-champagne shrink-0" />
                                                {d.name}
                                            </td>
                                            <td className="px-5 py-3">
                                                <button
                                                    onClick={() => copy(d.email)}
                                                    className="inline-flex items-center gap-2 text-brand-ink/80 hover:text-brand-navy"
                                                    title="Copy email"
                                                    data-testid={`admin-download-copy-${d.id}`}
                                                >
                                                    <Mail size={12} className="text-brand-champagne" />
                                                    {d.email}
                                                    <Copy size={11} className="opacity-60" />
                                                </button>
                                            </td>
                                            <td className="px-5 py-3 text-brand-ink/75">{d.organisation || <span className="text-brand-ink/40">—</span>}</td>
                                            <td className="px-5 py-3 text-brand-ink/75">{d.role || <span className="text-brand-ink/40">—</span>}</td>
                                            <td className="px-5 py-3 text-xs text-brand-ink/55">
                                                {new Date(d.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                                                <br/>
                                                <span className="text-brand-ink/40">{new Date(d.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
                                            </td>
                                            <td className="px-5 py-3 text-right">
                                                <button
                                                    onClick={() => deleteDownload(d.id)}
                                                    disabled={busyId === d.id}
                                                    data-testid={`admin-download-delete-${d.id}`}
                                                    className="text-xs text-brand-ink/50 hover:text-red-600 inline-flex items-center gap-1"
                                                >
                                                    {busyId === d.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </section>
            )}
        </div>
    );
}
